import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Card,
  Button,
  Dialog,
  Autocomplete,
  Box,
  Tooltip,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import {
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../../Components/Validation/validationUtils";
import {
  useCreateLocationMutation,
  useGetallEnumSearchQuery,
} from "../../../Api/attoDeskApi";
import {
  IEnumType,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import { EnumValuType } from "../../../Core/Enum/enum";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IStudentLocation;
}

// Modern SaaS-style preset palette (HEX values saved as-is to location_color).
const PRESET_COLORS: { name: string; value: string }[] = [
  { name: "Blue", value: "#2563EB" },
  { name: "Emerald", value: "#10B981" },
  { name: "Purple", value: "#7C3AED" },
  { name: "Orange", value: "#F97316" },
  { name: "Rose", value: "#E11D48" },
  { name: "Cyan", value: "#0891B2" },
  { name: "Indigo", value: "#4F46E5" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Sky", value: "#38BDF8" },
  { name: "Amber", value: "#FBBF24" },
  { name: "Pink", value: "#F472B6" },
  { name: "Slate", value: "#475569" },
];

const SWATCH_SIZE = 30;

interface IColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
}

const ColorPicker = ({ value, onChange }: IColorPickerProps) => {
  const normalized = (value || "").toLowerCase();
  const isPreset = PRESET_COLORS.some((c) => c.value.toLowerCase() === normalized);
  // Any non-empty value that isn't one of the presets counts as a custom color.
  const isCustomActive = Boolean(normalized) && !isPreset;
  // Fallback for the native picker so it opens on a valid colour.
  const customValue = /^#[0-9a-f]{6}$/i.test(value) ? value : "#000000";

  const swatchBase = {
    width: SWATCH_SIZE,
    height: SWATCH_SIZE,
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.12s ease, box-shadow 0.12s ease",
    outline: "none",
    "&:focus-visible": {
      boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.45)",
    },
  } as const;

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 1.5 }}>
        {PRESET_COLORS.map((color) => {
          const selected = normalized === color.value.toLowerCase();
          return (
            <Tooltip key={color.value} title={color.name} arrow>
              <Box
                role="radio"
                aria-checked={selected}
                aria-label={color.name}
                tabIndex={0}
                onClick={() => onChange(color.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange(color.value);
                  }
                }}
                sx={{
                  ...swatchBase,
                  backgroundColor: color.value,
                  transform: selected ? "scale(1.12)" : "scale(1)",
                  boxShadow: selected
                    ? `0 0 0 2px #fff, 0 0 0 4px ${color.value}`
                    : "0 1px 3px rgba(0,0,0,0.18)",
                  "&:hover": { transform: "scale(1.12)" },
                }}
              >
                {selected && (
                  <CheckIcon sx={{ color: "#fff", fontSize: 18 }} />
                )}
              </Box>
            </Tooltip>
          );
        })}

        {/* Custom colour — keeps the native picker as a secondary option. */}
        <Tooltip title="Custom colour" arrow>
          <Box
            component="label"
            aria-label="Custom colour"
            sx={{
              ...swatchBase,
              position: "relative",
              background: isCustomActive
                ? customValue
                : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
              transform: isCustomActive ? "scale(1.12)" : "scale(1)",
              boxShadow: isCustomActive
                ? `0 0 0 2px #fff, 0 0 0 4px ${customValue}`
                : "0 1px 3px rgba(0,0,0,0.18)",
              "&:hover": { transform: "scale(1.12)" },
            }}
          >
            {isCustomActive ? (
              <CheckIcon sx={{ color: "#fff", fontSize: 18 }} />
            ) : (
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: "16px",
                  textAlign: "center",
                  color: "#333",
                }}
              >
                +
              </Box>
            )}
            <input
              type="color"
              value={customValue}
              onChange={(e) => onChange(e.target.value)}
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                border: 0,
                opacity: 0,
                overflow: "hidden",
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      {/* Live preview */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "4px",
            backgroundColor: value || "transparent",
            border: "1px solid rgba(0,0,0,0.15)",
          }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13 }}>
          {value ? value.toUpperCase() : "No colour selected"}
        </Typography>
      </Box>
    </Box>
  );
};

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useCreateLocationMutation();
  const isMobileRes = useIsMobile();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik<IStudentLocation>({
    initialValues: {
      id: Number(data?.id),
      location_name: data?.location_name || "",
      location_address: data?.location_address || "",
      contact_number: data?.contact_number
        ? formatPhoneNumberForDisplay(data.contact_number)
        : "",
      location_color: data?.location_color || "",
      country_id: data?.country_id ?? null,
      province_id: data?.province_id ?? null,
      city_id: data?.city_id ?? null,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      const mobileNumberError = validatePhoneNumber(values.contact_number);
      if (mobileNumberError) {
        errors.contact_number = mobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          location_name: toProperCase(values.location_name),
          location_address: values.location_address,
          location_color: values.location_color,
          contact_number: values.contact_number.replace(/-/g, ""),
          country_id: values.country_id ?? null,
          province_id: values.province_id ?? null,
          city_id: values.city_id ?? null,
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  // Country > Province > City reference lists, each filtered by the selection above.
  const { data: countryData } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: EnumValuType.country,
  });
  const { data: provinceData } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: EnumValuType.province,
  });
  const { data: cityData } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: EnumValuType.city,
  });

  const countryList: IEnumType[] = Array.isArray(countryData?.data)
    ? (countryData?.data as IEnumType[])
    : [];
  const provinceList: IEnumType[] = Array.isArray(provinceData?.data)
    ? (provinceData?.data as IEnumType[])
    : [];
  const cityList: IEnumType[] = Array.isArray(cityData?.data)
    ? (cityData?.data as IEnumType[])
    : [];

  const filteredProvinces = useMemo(
    () =>
      provinceList.filter(
        (p) => Number(p.parent_id) === Number(formik.values.country_id)
      ),
    [provinceList, formik.values.country_id]
  );
  const filteredCities = useMemo(
    () =>
      cityList.filter(
        (c) => Number(c.parent_id) === Number(formik.values.province_id)
      ),
    [cityList, formik.values.province_id]
  );

  const formValid = useMemo(() => {
    return formik.values.location_name === "" ||
      formik.values.location_name === undefined ||
      formik.values.location_color === "" ||
      formik.values.location_color === undefined ||
      !formik.values.city_id
      ? false
      : true;
  }, [formik]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="md"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={1}
              sx={{ padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>
                  {data ? "Edit Location" : "Create Location"}
                </Typography>
              </Grid>
              <Grid
                item
                pt={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Location Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Location Name"
                                size="small"
                                {...formik.getFieldProps("location_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Color{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <ColorPicker
                                value={formik.values.location_color}
                                onChange={(hex) =>
                                  formik.setFieldValue("location_color", hex)
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Location Address
                                {/* <span className={classes.requiedfield}>*</span> */}
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Location Address"
                                size="small"
                                {...formik.getFieldProps("location_address")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Country
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Autocomplete
                                options={countryList}
                                getOptionLabel={(option: IEnumType) =>
                                  option.enum_value
                                }
                                value={
                                  countryList.find(
                                    (c) => c.id === formik.values.country_id
                                  ) || null
                                }
                                onChange={(_, newValue: IEnumType | null) => {
                                  formik.setFieldValue(
                                    "country_id",
                                    newValue?.id ?? null
                                  );
                                  // Reset dependent selections.
                                  formik.setFieldValue("province_id", null);
                                  formik.setFieldValue("city_id", null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Country"
                                    size="small"
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Province
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Autocomplete
                                options={filteredProvinces}
                                disabled={!formik.values.country_id}
                                getOptionLabel={(option: IEnumType) =>
                                  option.enum_value
                                }
                                value={
                                  filteredProvinces.find(
                                    (p) => p.id === formik.values.province_id
                                  ) || null
                                }
                                onChange={(_, newValue: IEnumType | null) => {
                                  formik.setFieldValue(
                                    "province_id",
                                    newValue?.id ?? null
                                  );
                                  formik.setFieldValue("city_id", null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={
                                      formik.values.country_id
                                        ? "Select Province"
                                        : "Select a country first"
                                    }
                                    size="small"
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                City
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Autocomplete
                                options={filteredCities}
                                disabled={!formik.values.province_id}
                                getOptionLabel={(option: IEnumType) =>
                                  option.enum_value
                                }
                                value={
                                  filteredCities.find(
                                    (c) => c.id === formik.values.city_id
                                  ) || null
                                }
                                onChange={(_, newValue: IEnumType | null) => {
                                  formik.setFieldValue(
                                    "city_id",
                                    newValue?.id ?? null
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={
                                      formik.values.province_id
                                        ? "Select City"
                                        : "Select a province first"
                                    }
                                    size="small"
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Contact Number
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Contact Number"
                                size="small"
                                value={formik.values.contact_number}
                                onChange={(e) =>
                                  handlePhoneChange(
                                    "contact_number",
                                    e.target.value,
                                    formik.setFieldValue
                                  )
                                }
                                onBlur={formik.handleBlur}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.contact_number &&
                                  Boolean(formik.errors.contact_number)
                                }
                                helperText={
                                  formik.touched.contact_number &&
                                  formik.errors.contact_number
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          className={classes.addButton}
                          disabled={!formValid || isLoading}
                        >
                          {isLoading && (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                              sx={{ marginRight: 1 }}
                            />
                          )}
                          {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
